import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { Dropdown, Input, MenuProps, Modal } from "antd";
import axios, { AxiosError, AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { RoutePath } from "src/data/Routes";
import { MenuNode } from "src/model/MenuNode";
import { postApi, putApi } from "src/util/apiCalls";
import { Log } from '../../service/log.service';

enum Context {
  FOLDER = 'folder', RENAME = 'rename', DELETE = 'delete', LINK = 'link'
}

export interface ContextState {
  folders: MenuDataNode[];
}

export interface MenuDataNode extends MenuNode {
  loading?: boolean;
  children?: MenuDataNode[];
  titleText?: string;
  key: string;
  selected?: boolean;
  label?: string;
  value?: string;
  fullPath?: string;
}

/**
 * Builds a human readable folder path for display, e.g. "/ folder1 / folder2".
 * The root folder is "/", and each nested segment is separated by " / ".
 */
export const buildFolderPath = (parentPath: string | undefined, label: string): string =>
  parentPath === '/' ? `/ ${label}` : parentPath ? `${parentPath} / ${label}` : label;

export interface FolderMenuConfig {
  getState: () => { folders: MenuDataNode[]; };
  setState: (state: { folders: MenuDataNode[]; }) => void;
  onDelete?: (item: MenuDataNode) => void;
  isNavMenu?: boolean;

  createUrl: string;
  getIdFromResponse: (data: any) => string;
  getItemDataUrl: (id: string) => string;
  getItemMenuUrl: (id: string, hwType?: string) => string;
  renameUrl: (itemId: string) => string;
  deleteUrl: (itemId: string) => string;

  reload: (serverRefetch?: boolean) => void;
  deleteContentLabel?: string;
  linkRoute?: string;
  createBody?: Record<string, any>;
}

export class FolderContextMenuBase {

  static readonly ROOT = 'root';

  static keepOpen: boolean;
  static selectRef: { focus(): void; };
  static updateCaller: (load?: boolean) => void;

  getState: () => ContextState;
  onFolderCreated?: (id: string) => void;

  constructor(protected readonly cfg: FolderMenuConfig) {
    this.getState = cfg.getState;
  }

  static equals(first: MenuDataNode, second: MenuDataNode) {
    if (first === second) {
      return true;
    } else if (first && second) {
      return first.itemId && first.itemId === second.itemId || first.key && first.key === second.key;
    } else if (!first) {
      return !second;
    }
    return false;
  }

  createLabel(item: MenuDataNode) {
    return <Dropdown menu={this.contextMenu(item)} trigger={['contextMenu']}><div>{item.titleText ? item.titleText : item.label}</div></Dropdown>;
  }

  private returnFocus(load?: boolean) {
    FolderContextMenuBase.selectRef?.focus();
    FolderContextMenuBase.keepOpen = false;
    FolderContextMenuBase.updateCaller?.(load);
  }

  private createItemDialog(item: MenuDataNode, type: Context) {
    let name = '';
    Modal.confirm({
      title: <span>Create new {type} inside <strong>{item.titleText || item.title}</strong></span>,
      autoFocusButton: null,
      icon: null,
      zIndex: 2000,
      content: <Input autoFocus={true} placeholder="Enter new name"
        ref={r => {
          if (!r) return;
          setTimeout(() => {
            r.input.focus();
          });
        }}
        onChange={(event) => name = event.target.value.trim()}
        onPressEnter={(event) => event.currentTarget.closest('.ant-modal-body').querySelector<HTMLButtonElement>('.ant-btn-primary').click()}></Input>,
      okText: 'Create',
      onOk: () => {
        if (!name?.length) {
          const warn = 'Name cannot be empty!';
          Log.warn(warn);
          return Promise.reject(new Error(warn));
        }
        const parent = this.getFresh(item, this.getState().folders);

        if (parent?.children?.some(mdn => mdn.titleText === name)) {
          const warnMessage = `Name ${name} already exists inside ${parent.titleText}`;
          Log.warn(warnMessage);
          return Promise.reject(new Error(warnMessage));
        }
        const group_id = MenuNode.getGroupId(item);
        return postApi<any, any>(this.cfg.createUrl,
          {
            label: name,
            parent: item.itemId === FolderContextMenuBase.ROOT || item.group_id ? null : item.itemId,
            group_id,
            hw_type: Array.isArray(item.hwType) ? item.hwType[0] : item.hwType,
            ...this.cfg.createBody
          }).then(response => {
            const data = response.data.data;
            const id = this.cfg.getIdFromResponse(data);
            const mdn: MenuDataNode = {
              label: null, key: id, value: id, itemId: id, title: data.label, titleText: data.label,
              fullPath: buildFolderPath(item.itemId === FolderContextMenuBase.ROOT ? '/' : item.fullPath, data.label),
              isLeaf: !this.cfg.isNavMenu, children: [],
              isGroupFolder: this.cfg.isNavMenu || undefined,
              url: this.cfg.isNavMenu ? this.cfg.getItemMenuUrl(id, Array.isArray(item.hwType) ? item.hwType[0] : item.hwType) : this.cfg.getItemDataUrl(id)
            };
            mdn.title = this.createLabel(mdn);
            const fresh = this.getFresh(item, this.getState().folders);
            if (fresh) {
              if (!fresh.children?.length) {
                fresh.children = [mdn];
              } else {
                const groupIdx = fresh.children.findIndex(mdn => mdn.key === MenuNode.GROUPS_DIVIDER_KEY);
                if (groupIdx >= 0) {
                  fresh.children = [...fresh.children.slice(0, groupIdx), mdn, ...fresh.children.slice(groupIdx)];
                } else
                  fresh.children = [...fresh.children || [], mdn];
              }
              fresh.isLeaf = false;
              this.updateFolders([...this.getState().folders]);
            } else if (item.itemId === FolderContextMenuBase.ROOT) {
              let folders = this.getState().folders;
              const groupIdx = folders.findIndex(mdn => mdn.key === MenuNode.GROUPS_DIVIDER_KEY);
              if (groupIdx >= 0) {
                folders = [...folders.slice(0, groupIdx), mdn, ...folders.slice(groupIdx)];
              } else
                folders = [...folders, mdn];
              this.updateFolders(folders);
            }
            this.onFolderCreated?.(id);

          }, (reason: AxiosError) => Log.error(`Failed to create ${type} ${name}`, reason)).finally(() => this.returnFocus());
      },
      onCancel: () => this.returnFocus()
    });
  }

  private getFresh(item: MenuDataNode, nodes: MenuDataNode[]): MenuDataNode {
    if (!item || !nodes) return;
    for (const node of nodes) {
      if (FolderContextMenuBase.equals(item, node)) return node;
      const result = this.getFresh(item, node.children);
      if (result) return result;
    }
  }

  private filterItemRekur(item: MenuDataNode, nodes: MenuDataNode[]): MenuDataNode[] {
    if (!nodes || !item)
      return nodes;
    const result: MenuDataNode[] = [];
    for (const node of nodes) {
      if (FolderContextMenuBase.equals(item, node)) {
        return nodes.filter(mdn => !FolderContextMenuBase.equals(item, mdn));
      }
      node.children = this.filterItemRekur(item, node.children);
      if (!node.children?.length) node.isLeaf = true;
      result.push(node);
    }
    return result;
  }

  private updateFolders(folders: MenuDataNode[], serverRefetch?: boolean) {
    this.cfg.setState({ folders });
    this.cfg.reload(serverRefetch);
  }

  private getChildren(item: MenuDataNode, isNavMenu?: boolean): MenuProps['items'] {
    return [
      { key: Context.FOLDER, disabled: isNavMenu && item.isLeaf, label: 'Create folder' },
      { key: Context.RENAME, disabled: isNavMenu && item.isLeaf || item.itemId === FolderContextMenuBase.ROOT || !!item.group_id, label: 'Rename' },
      { key: Context.DELETE, disabled: isNavMenu && item.isLeaf || item.itemId === FolderContextMenuBase.ROOT || !!item.group_id, label: 'Delete' },
      isNavMenu && { key: 'divider', type: 'divider' },
      isNavMenu && this.cfg.linkRoute && { key: Context.LINK, label: <Link to={this.cfg.linkRoute}>Configure</Link> }
    ];
  }

  private contextMenu(item: MenuDataNode): MenuProps {
    const type = Context.FOLDER;
    const { isNavMenu } = this.cfg;
    return {
      onClick: info => {
        switch (info.key) {
          case Context.FOLDER:
            FolderContextMenuBase.keepOpen = true;
            this.createItemDialog(item, info.key);
            break;
          case Context.RENAME:
            FolderContextMenuBase.keepOpen = true;
            let name = item.titleText || item.title as string;
            Modal.confirm({
              title: 'Rename ' + type,
              autoFocusButton: null,
              icon: null,
              zIndex: 2000,
              content: <Input autoFocus={true} placeholder="Enter new name" defaultValue={name}
                ref={r => {
                  if (!r) return;
                  setTimeout(() => {
                    r.input.focus();
                  });
                }}
                onChange={(event) => name = event.target.value.trim()}
                onPressEnter={(event) => event.currentTarget.closest('.ant-modal-body').querySelector<HTMLButtonElement>('.ant-btn-primary').click()}></Input>,
              okText: 'Rename',
              onOk: () => {
                if (!name?.length) {
                  const warnMessage = 'Name cannot be empty!';
                  Log.warn(warnMessage);
                  return Promise.reject(new Error(warnMessage));
                }
                const parent = this.getFresh(item, this.getState().folders);
                if (parent?.children?.some(mdn => mdn.titleText === name)) {
                  const warnMessage = `Name ${name} already exists inside ${parent.titleText}`;
                  Log.warn(warnMessage);
                  return Promise.reject(new Error(warnMessage));
                }
                return putApi<any, { label: string; }>(this.cfg.renameUrl(item.itemId), { label: name }).then(() => {
                  const fresh = this.getFresh(item, this.getState().folders);
                  fresh.titleText = name;
                  fresh.title = this.createLabel(fresh);
                  this.updateFolders([...this.getState().folders]);
                }, (reason: AxiosError) => Log.error(`Failed to rename ${type} ${item.titleText}`, reason)).finally(() => this.returnFocus());
              },
              onCancel: () => this.returnFocus()
            });
            break;
          case Context.DELETE:
            FolderContextMenuBase.keepOpen = true;
            Modal.confirm({
              title: 'Confirm delete',
              autoFocusButton: null,
              zIndex: 2000,
              content: <span>Are you sure to delete {type} <strong>{item.titleText || item.title}</strong>{this.cfg.deleteContentLabel ? <> and <strong>all</strong> {this.cfg.deleteContentLabel} it contains</> : null}?</span>,
              okText: 'Delete',
              onOk: () => {
                return axios.delete<AxiosResponse<BasicResponseDTO>>(this.cfg.deleteUrl(item.itemId)).then(() => {
                  const fresh = this.getFresh(item, this.getState().folders);
                  if (fresh) fresh.selected = false;
                  this.cfg.onDelete?.(item);
                  this.updateFolders(this.filterItemRekur(item, this.getState().folders), true);
                  this.returnFocus(true);
                }, (reason: AxiosError) => {
                  Log.error(`Failed to delete ${type} ${item.titleText}`, reason);
                  this.returnFocus();
                });
              },
              onCancel: () => this.returnFocus()
            });
            break;
          case Context.LINK:
            break;
          default:
            Log.warn('Unknown context menu ' + info.key);
        }
      }, items: this.cfg.linkRoute?.endsWith(RoutePath.SET_CG) ? [{ type: 'group', children: this.getChildren(item, isNavMenu) }] : [
        {
          key: 'groupMenu', label: `${this.cfg.linkRoute?.endsWith(RoutePath.SET_CG) ? "" : item.group_id ? 'User Group' : 'Folder'} ${item.titleText || item.title as string}`, children: this.getChildren(item, isNavMenu)
        }
      ]
    };
  }
}
